import React from "react";
import fileDownload from "js-file-download";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import { Button } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import DataTable from "./DataTable";
import Title from "./Title";
import TerminateBatch from "./TerminateBatch";

import { copyData } from "../../utils/copy";
import useAxios from "../../hooks/useAxios";
import useAlert from "../../hooks/useAlert";
import useAuth from "../../hooks/useAuth";
import useBatchDetailState from "../../hooks/useBatchDetailState";
import { __export, __retryOperation, __checkStatus } from "../../api/sim";

/**
 * Shared single-batch line-list page. Replaces the ~80 near-identical
 * `*Batch.js` + `*List.js` pairs (one per carrier x operation) that each
 * hand-wired DataTable, Export/Retry/Copy/Terminate buttons, and batch_id
 * fetching. Configured per operation via props; feature flags/slots below
 * control which action buttons render (not every operation has every action).
 */
const BatchDetail = ({
  title,
  apiGet,
  client,
  columnsFactory,
  copyKeys,
  requestExtraParams,
  exportAction,
  exportFilename,
  retryOperation,
  showTerminate = false,
  terminateOperation,
  showCheckStatus = false,
  checkStatusOperation,
  updateAndRetry,
  extraActions,
  getRowPath,
  disableRowClick = false,
  disableLatestOnly = false,
}) => {
  const nav = useNavigate();
  const { setAlertMessage } = useAlert();
  const { user } = useAuth();

  const {
    batch_id,
    data,
    loading,
    metaData,
    response,
    latestOnly,
    handleLatestOnlyChange,
  } = useBatchDetailState(apiGet, { client, extraParams: requestExtraParams });

  const [exportResponse, exportCall, exportLoading] = useAxios({ ...__export });
  const [retryResponse, retryCall, retryLoading] = useAxios({
    ...__retryOperation(batch_id),
  });
  const [checkStatusResponse, checkStatusCall, checkStatusLoading] = useAxios({
    ...__checkStatus(batch_id),
  });

  React.useEffect(() => {
    if (exportResponse && exportResponse.status) {
      fileDownload(
        exportResponse.data,
        exportFilename ? exportFilename(batch_id) : `${exportAction}_export-${batch_id}.csv`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportResponse]);

  React.useEffect(() => {
    if (retryResponse && retryResponse.status) {
      setAlertMessage({ type: "success", message: "Retry queued" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryResponse]);

  React.useEffect(() => {
    if (checkStatusResponse && checkStatusResponse.status) {
      setAlertMessage({ type: "success", message: "Status recheck queued" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkStatusResponse]);

  const handleColumnClick = (key) => copyData(data, [key]);
  const handleRowClick = getRowPath
    ? (params) => nav(getRowPath(params.row))
    : null;

  const columns = columnsFactory({ handleColumnClick, client: true });

  const showUpdateAndRetry =
    updateAndRetry && updateAndRetry.getValidRetryLines(data).length > 0;

  const anyLoading = loading || exportLoading || retryLoading || checkStatusLoading;

  return (
    <Box sx={{ width: "100%" }}>
      <Title variant="h5" content={`${title} Batch ID: ${batch_id}`} gutterBottom />
      {response && (
        <Grid container justifyContent="flex-start" spacing={2}>
          {!disableLatestOnly && user.admin && (
            <Grid item sx={{ mb: "10px" }}>
              <Tooltip title="Hide lines whose identity (ICCID/IMSI/MSISDN) has a newer submission in a later batch">
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ cursor: "pointer" }}
                  onClick={handleLatestOnlyChange}
                >
                  <Checkbox checked={latestOnly} onClick={(e) => e.stopPropagation()} onChange={handleLatestOnlyChange} />
                  <span>Latest Only</span>
                </Stack>
              </Tooltip>
            </Grid>
          )}
          {exportAction && (
            <Grid item sx={{ mb: "10px" }}>
              <Stack spacing={2} direction="row">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={anyLoading}
                  onClick={() =>
                    exportCall({
                      responseType: "blob",
                      params: { action: exportAction, batch_id, client },
                    })
                  }
                >
                  <FileDownloadIcon sx={{ mr: "5px" }} /> Export
                </Button>
              </Stack>
            </Grid>
          )}
          <Grid item sx={{ mb: "10px" }}>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                color="secondary"
                disabled={anyLoading}
                onClick={() => copyData(data, copyKeys)}
              >
                <ContentCopyIcon sx={{ mr: "5px" }} /> Copy Data
              </Button>
            </Stack>
          </Grid>
          {retryOperation && (
            <Grid item sx={{ mb: "10px" }}>
              <Stack spacing={2} direction="row">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={anyLoading}
                  onClick={() =>
                    retryCall({ params: { client, operation: retryOperation } })
                  }
                >
                  Retry
                </Button>
              </Stack>
            </Grid>
          )}
          {showUpdateAndRetry && (
            <Grid item sx={{ mb: "10px" }}>
              <Stack spacing={2} direction="row">
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={anyLoading}
                  onClick={() => nav(updateAndRetry.path || "update")}
                >
                  Update and Retry
                </Button>
              </Stack>
            </Grid>
          )}
          {showCheckStatus && (
            <Grid item sx={{ mb: "10px" }}>
              <Stack spacing={2} direction="row">
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={anyLoading}
                  onClick={() => checkStatusCall({ params: { operation: checkStatusOperation } })}
                >
                  Check Status
                </Button>
              </Stack>
            </Grid>
          )}
          {showTerminate && (
            <Grid item sx={{ mb: "10px" }}>
              <TerminateBatch batch_id={batch_id} operation={terminateOperation} />
            </Grid>
          )}
          {extraActions && extraActions({ batch_id, data })}
        </Grid>
      )}
      {response ? (
        <DataTable
          disableColumnFilter
          data={data}
          columns={columns}
          loading={loading}
          metaData={{ ...metaData, page_size: 100 }}
          client={true}
          handleRowClick={disableRowClick ? null : handleRowClick}
        />
      ) : (
        <Title content="Please wait..." variant="subtitle2" gutterBottom />
      )}
    </Box>
  );
};

export default BatchDetail;
